import { createContext, useMemo, useContext } from 'react';
import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import { RouterLink } from '../Link';
import styles from './SidebarNav.module.less';

const SidebarNavContextActiveKey = createContext<string>('');
const useSidebarNavActiveKey = () => useContext(SidebarNavContextActiveKey);

//  This is a simple hook that will return the last path segment of a given pathname, if it exists.
function useActiveKey(pathname?: string): string {
  return useMemo(() => pathname?.split('/').pop() ?? '', [pathname]);
}

type SidebarNavItemProps = {
  /**
   * Where the link should navigate to
   */
  to: string;
  /**
   * If not providing the pathname to SidebarNav, you can manually set a nav item to be active
   */
  isActive?: boolean;
  /**
   * If you are providing a pathname to SidebarNav, this is the key it will attempt to match on.
   */
  activeKey?: string;
  /**
   * An optional onClick handler
   */
  onClick?: () => void;
};

type SidebarNavComponent = FunctionComponent<{ pathname?: string; }> & {
  Heading: FunctionComponent;
  Item: FunctionComponent<SidebarNavItemProps>;
};

/**
 * SidebarNav is a set of navigation components which allows
 * you to build a vertically stacked navigation, which are typically
 * used in a SidebarNavLayout.
 *
 * An opt-in feature is to provide the `location.pathname` and an `activeKey`
 * prop to each item. This will use the last path segment to try to automatically
 * determine if the item is active or not. EG: activeKey="home" will match on
 * routes ending with "/home".
 *
 * @example
 * <SidebarNav>
 *   <SidebarNav.Heading>
 *     Settings
 *   </SidebarNav.Heading>
 *   <SidebarNav.Item to="/home" isActive>
 *     Home
 *   </SidebarNav.Item>
 *   <SidebarNav.Item to="/about">
 *     About
 *   </SidebarNav.Item>
 *   <SidebarNav.Item to="/contact">
 *     Contact
 *   </SidebarNav.Item>
 * </SidebarNav>
 */
const SidebarNav: SidebarNavComponent = ({ children, pathname }) => {
  const header = findFirstForSlot('heading', children);
  const items = findAllForSlot('item', children);
  return (
    <SidebarNavContextActiveKey.Provider value={useActiveKey(pathname)}>
      <nav
        className={styles.nav}
        aria-label="Sidebar Navigation"
      >
        {header}
        <ul
          className={styles.list}
          role="menubar"
          aria-label="Sidebar Navigation"
          aria-orientation="vertical"
        >
          {items}
        </ul>
      </nav>
    </SidebarNavContextActiveKey.Provider>
  );
};

SidebarNav.Heading = defineSlotComponent('heading', ({ children }) => (
  <h3 className={styles.heading}>
    {children}
  </h3>
));

SidebarNav.Item = defineSlotComponent('item', ({ children, to, isActive, activeKey, onClick }) => {
  const contextActiveKey = useSidebarNavActiveKey();
  const shouldUseContextToSetActive = !!contextActiveKey && !!activeKey;
  return (
    <li
      role="none"
      className={styles.item}
    >
      <RouterLink
        to={to}
        onClick={onClick}
        role="menuitem"
        //  Normally we wouldn't apply the className directly, but it's fine in this case
        className={cx(styles.itemLink, {
          //  isActive is the controlled prop, so it should take priority.
          [styles.itemLinkActive]: isActive || (shouldUseContextToSetActive && contextActiveKey === activeKey),
        })}
        tabIndex={0}
      >
        {children}
      </RouterLink>
    </li>
  );
});

export default SidebarNav;
